import { useState, useEffect, useMemo } from 'react';
import CampaignBuilder from '../apps/recruiter/components/Needs/CampaignBuilder';
import CenterVisuals from '../apps/recruiter/components/Needs/CenterVisuals';
import ZonesController from '../apps/recruiter/components/Plan/ZonesController';
import ContextBadge from '../components/layout/ContextBadge';
import TourButton from '../components/tour/TourButton';
import ForecastSourceToggle from '../apps/recruiter/components/ForecastSourceToggle';
import { useAuth } from '../context/AuthContext';
import { useHourlyLocations, useHourlyRoles } from '../hooks/queries/useHourlyData';
import {
  useForecastSourceVersion,
  getForecastSource,
  getForecastLocationId,
} from '../apps/recruiter/state/forecastSource';

const AVAILABLE_JOBS = ["Server", "Cook", "Bartender", "Security", "Dishwasher", "Manager", "Cleaner", "Barista"];

export default function ForecastView() {
  // Auth
  const { hourlyToken } = useAuth();
  const hasHourlyToken = Boolean(hourlyToken);

  // Hourly data (only fetched when authenticated)
  const { data: locations = [], isLoading: locationsLoading } = useHourlyLocations(hasHourlyToken);
  const { data: hourlyRoles = [] } = useHourlyRoles(hasHourlyToken);

  // Forecast source state (re-renders on toggle)
  const _sourceVersion = useForecastSourceVersion();
  const dataSource = getForecastSource();
  const forecastLocationId = getForecastLocationId();

  // Resolve hourly IDs from store/roles
  const hourlyLocationId = useMemo(() => forecastLocationId, [forecastLocationId]);

  const hourlyJobRoleFamilyId = useMemo(() => {
    if (!hourlyRoles.length) return null;
    return (hourlyRoles[0] as { jobRoleFamilyId?: string })?.jobRoleFamilyId ?? null;
  }, [hourlyRoles]);

  const [zones, setZones] = useState({ lowRed: 70, lowYellow: 95, highYellow: 120, highRed: 150 });
  
  // Range selector state with localStorage persistence
  const [needsRangeIdx, setNeedsRangeIdx] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('passcom-needs-range-idx');
      if (saved) {
        const parsed = Number(saved);
        if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 3) {
          return parsed;
        }
      }
    } catch {}
    return 1; // Default to "Quarter"
  });

  // Persist range selection to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('passcom-needs-range-idx', String(needsRangeIdx));
    } catch {}
  }, [needsRangeIdx]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center py-3">
            <h1 className="text-xl font-bold text-gray-900">Plan your campaign</h1>
            <div className="flex items-center gap-4">
              <ForecastSourceToggle
                locations={locations}
                locationsLoading={locationsLoading}
                hasHourlyToken={hasHourlyToken}
              />
              <TourButton />
              <ContextBadge />
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="h-full max-w-7xl mx-auto px-6 py-8">
          <div className="h-full grid gap-6 grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)]">
            {/* Left Column: Campaign Builder */}
            <section className="bg-white border rounded-xl shadow-sm p-0 overflow-hidden">
              <CampaignBuilder
                zones={zones}
                onZonesChange={setZones}
                availableRoles={dataSource === 'live' && hourlyRoles.length > 0 ? hourlyRoles.map(r => r.name) : undefined}
                availableLocations={dataSource === 'live' && locations.length > 0 ? locations.map(l => l.name) : undefined}
              />
            </section>
            
            {/* Right Column: Zones and Visuals */}
            <div className="flex flex-col gap-6">
              {/* Zones Control at Top */}
              <section id="tour-zones" className="bg-white border rounded-xl shadow-sm px-4 py-3">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Zones</h2>
                  <p className="text-xs text-gray-500 flex-1">Set warning levels for both oversupply and undersupply of labor</p>
                  <button
                    className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                    onClick={() => setZones({ lowRed: 70, lowYellow: 95, highYellow: 120, highRed: 150 })}
                    type="button"
                  >
                    reset
                  </button>
                </div>
                <ZonesController zones={zones} onChange={setZones} />
              </section>
              
              {/* Center Visuals Below */}
              <section className="bg-white border rounded-xl shadow-sm p-4 flex-1 min-h-0">
                <CenterVisuals
                  job={AVAILABLE_JOBS[0]}
                  rangeIdx={needsRangeIdx}
                  onRangeChange={setNeedsRangeIdx}
                  zones={zones}
                  dataSource={dataSource}
                  hourlyLocationId={hourlyLocationId}
                  hourlyJobRoleFamilyId={hourlyJobRoleFamilyId}
                />
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

